'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'

interface UseHeightResizeOptions {
  minHeight?: number
  maxHeight?: number
  initialHeight?: number
  headerHeight?: number
  bottomNavHeight?: number
  /**
   * When false, the hook skips all setup (event listeners, state) and returns
   * no-op values. Useful when the resize behavior is conditionally needed.
   */
  enabled?: boolean
}

// No-op function for disabled state
const noop = () => {}

/**
 * The starting height, held to the viewport the way a dragged height is.
 *
 * Consumers size `initialHeight` to their content, which can be taller than the
 * window, and before this the initial value was the only height that never met
 * a bound — `getMaxHeight` is applied on drag and keyboard resize but was never
 * applied on mount.
 *
 * Re-run whenever the viewport changes, for as long as the member hasn't moved
 * the handle themselves — see the `resize`/`orientationchange` effect below.
 *
 * This is the viewport bound only. It deliberately does NOT account for a
 * phone's safe-area inset, because it can't: `--safe-top` reads back as an
 * unresolved `max(env(…), …)` token and `env()` isn't visible to script at all,
 * so the inset is knowable only to the browser. `Poptart` therefore folds the
 * safe area into the inline `max-height` it builds from this number (see
 * `getMaxHeightStyle`), where the browser resolves it. Consumers that render
 * the value some other way need to do the same.
 *
 * Falls through to the raw value when there's no window (SSR): the height only
 * matters once a poptart is open, which is a client interaction, so the server
 * pass has nothing to clamp against.
 */
const getClampedInitialHeight = ({
  initialHeight,
  minHeight,
  maxHeight,
  headerHeight,
  bottomNavHeight,
}: Required<
  Pick<
    UseHeightResizeOptions,
    'initialHeight' | 'minHeight' | 'headerHeight' | 'bottomNavHeight'
  >
> &
  Pick<UseHeightResizeOptions, 'maxHeight'>) => {
  if (typeof window === 'undefined') return initialHeight

  const layoutConstrainedHeight =
    window.innerHeight - headerHeight - bottomNavHeight
  const effectiveMaxHeight = maxHeight
    ? Math.min(maxHeight, layoutConstrainedHeight)
    : layoutConstrainedHeight

  // minHeight is honored even past the viewport, which is the one place this
  // deliberately parts ways with the drag handlers: they apply the bounds the
  // other way round, `Math.min(Math.max(next, minHeight), getMaxHeight())`, so a
  // drag in a window shorter than the floor lands on the viewport instead. Both
  // are defensible for a window that can't fit the minimum; opening at a usable
  // size is the better of the two, and the member can drag from there.
  return Math.max(Math.min(initialHeight, effectiveMaxHeight), minHeight)
}

/**
 * @param {number} minHeight - Smallest the poptart can be
 * @param {number} maxHeight - Largest the poptart can be
 * @param {number} initialHeight - Default height of the poptart
 * @param {number} headerHeight - Height of the app header. The max height is
 *   decreased by this number.
 * @param {number} bottomNavHeight - Height of the bottom nav. The max height is
 *   decreased by this number.
 * @param {boolean} enabled - When false, skips all setup and returns no-op
 *   values
 * @returns
 */
export const useHeightResize = ({
  minHeight = 96,
  maxHeight,
  initialHeight = 480,
  headerHeight = 0,
  bottomNavHeight = 0,
  enabled = true,
}: UseHeightResizeOptions) => {
  // When disabled, return no-op values immediately to avoid setting up
  // event listeners and state that won't be used
  const [height, setHeight] = useState(() =>
    enabled
      ? getClampedInitialHeight({
          initialHeight,
          minHeight,
          maxHeight,
          headerHeight,
          bottomNavHeight,
        })
      : 0,
  )
  const [isResizing, setIsResizing] = useState(false)
  const [touchStart, setTouchStart] = useState(0)

  // Add a ref to track if we should handle the touch
  const shouldHandleTouch = useRef(false)

  // The height the member last set with the handle, or null while they haven't
  // touched it. The viewport listener below clamps from this rather than from the
  // current height, so a rotation into a landscape window doesn't permanently eat
  // a portrait choice — clamping `prev` only ever ratchets down, and nothing
  // resets it, so Safari's collapsing address bar would shrink the panel a step
  // at a time with no way back up.
  //
  // A ref, not state: only that listener reads it, and nothing about the render
  // changes when it moves. Written by the handlers that actually move the height,
  // not by mousedown/touchstart, so a press that never moves leaves the member
  // with no choice recorded.
  const memberHeightRef = useRef<number | null>(null)

  // Mirrors `isResizing` for the release listeners, which are registered once and
  // would otherwise close over the mount-time value.
  const isResizingRef = useRef(false)

  // Helper function to calculate the effective max height
  const getMaxHeight = useCallback(() => {
    const viewportHeight = window.innerHeight
    const layoutConstrainedHeight =
      viewportHeight - headerHeight - bottomNavHeight

    // If maxHeight is provided, use the minimum of maxHeight and layout-constrained height
    // Otherwise, use the layout-constrained height
    return maxHeight
      ? Math.min(maxHeight, layoutConstrainedHeight)
      : layoutConstrainedHeight
  }, [maxHeight, headerHeight, bottomNavHeight])

  // The one place a member-driven height is written, so the remembered choice and
  // the rendered height can't drift apart.
  const commitMemberHeight = useCallback(
    (next: number) => {
      const bounded = Math.min(Math.max(next, minHeight), getMaxHeight())
      memberHeightRef.current = bounded
      setHeight(bounded)
    },
    [minHeight, getMaxHeight],
  )

  const onMouseDown = useCallback((e?: React.MouseEvent) => {
    // Prevent text selection on mousedown (if it's a mouse event)
    if (e) {
      e.preventDefault()
    }
    isResizingRef.current = true
    setIsResizing(true)
  }, [])

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    shouldHandleTouch.current = true
    isResizingRef.current = true
    setIsResizing(true)
    setTouchStart(e.touches[0].clientY)
  }, [])

  const onKeyboardResize = useCallback(
    (e: React.KeyboardEvent) => {
      const STEP = 20 // pixels to resize per keystroke

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault()
          // Stepping from the rendered height rather than the remembered choice:
          // if the viewport has since clamped the panel, the member is arrowing
          // away from what they can see.
          commitMemberHeight(height + STEP)
          break
        case 'ArrowDown':
          e.preventDefault()
          commitMemberHeight(height - STEP)
          break
      }
    },
    [height, commitMemberHeight],
  )

  // The height above is seeded once, when the consumer mounts — for a poptart that
  // is app load, long before the member opens it. Whatever `window.innerHeight`
  // read then is what the panel opens at forever, so a rotation, a split-view
  // resize, or a mobile browser toolbar that collapses after the first scroll all
  // leave it sized for a viewport that no longer exists. Re-deriving on the
  // viewport's own events is what keeps the two in step.
  //
  // Once the member has dragged the handle, their height is the answer and this
  // only fits it to the new viewport — re-seeding would throw away a size they
  // chose deliberately. Fitted from what they chose, not from what is on screen
  // now, so a window that grows back gives the height back. The clamp is the drag
  // handlers' one, so a window shorter than `minHeight` lands where a drag would.
  useEffect(() => {
    if (!enabled) return

    const onViewportChange = () => {
      if (memberHeightRef.current !== null) {
        setHeight(
          Math.min(
            Math.max(memberHeightRef.current, minHeight),
            getMaxHeight(),
          ),
        )
        return
      }

      setHeight(
        getClampedInitialHeight({
          initialHeight,
          minHeight,
          maxHeight,
          headerHeight,
          bottomNavHeight,
        }),
      )
    }

    window.addEventListener('resize', onViewportChange)
    window.addEventListener('orientationchange', onViewportChange)

    return () => {
      window.removeEventListener('resize', onViewportChange)
      window.removeEventListener('orientationchange', onViewportChange)
    }
  }, [
    enabled,
    initialHeight,
    minHeight,
    maxHeight,
    headerHeight,
    bottomNavHeight,
    getMaxHeight,
  ])

  // Armed for as long as the hook is enabled, not only while a drag is in flight.
  // The *move* listeners below can afford to be attached after the fact — a drag
  // nobody has noticed yet simply moves nothing — but a release that arrives
  // before its listener is a resize that never ends: `isResizing` stays true and
  // the panel then follows the bare cursor around the screen, no button held.
  // Two ways that happens, both on desktop:
  //
  // - The release lands on a cross-origin iframe. Ash fills the poptart body, so
  //   a drag downward is over it immediately, and mouse events there belong to the
  //   iframe's document — ours never sees the `mouseup`. `Poptart` also drops the
  //   body's pointer events while resizing, which is what keeps the moves coming;
  //   this is the backstop for the release itself.
  // - The click is quick enough to release before React has committed the state
  //   change that would have attached the listener.
  //
  // `pointercancel` and `blur` cover the gestures the browser takes over outright
  // — a scroll it decides to own, a tab switch mid-drag — which produce no `up`.
  useEffect(() => {
    if (!enabled) return

    const stopResizing = () => {
      // Six listeners, for the hook's lifetime, on every mounted consumer — Ash
      // and the inbox both stay mounted — so without this every click on the page
      // would queue a no-op state update per poptart per release event.
      if (!isResizingRef.current) return

      isResizingRef.current = false
      shouldHandleTouch.current = false
      setIsResizing(false)
    }

    const releaseEvents = [
      'mouseup',
      'pointerup',
      'pointercancel',
      'touchend',
      'touchcancel',
      'blur',
    ]

    releaseEvents.forEach(event => window.addEventListener(event, stopResizing))

    return () =>
      releaseEvents.forEach(event =>
        window.removeEventListener(event, stopResizing),
      )
  }, [enabled])

  useEffect(() => {
    // Skip all event listener setup when disabled
    if (!enabled) return

    const onMouseMove = (e: MouseEvent) => {
      if (!isResizing) return

      commitMemberHeight(window.innerHeight - e.clientY)
    }

    const onTouchStartDoc = (e: TouchEvent) => {
      if (shouldHandleTouch.current) {
        e.stopPropagation()
      }
    }

    const onTouchMove = (e: TouchEvent) => {
      if (!isResizing || !shouldHandleTouch.current) return

      // If we initiated the touch, prevent page scrolling while resizing
      if (e.cancelable) {
        e.preventDefault()
      }

      const touchDelta = touchStart - e.touches[0].clientY
      commitMemberHeight(height + touchDelta)
      setTouchStart(e.touches[0].clientY)
    }

    // Prevent text selection during resize
    const preventSelection = (e: Event) => {
      if (isResizing) {
        e.preventDefault()
      }
    }

    if (isResizing) {
      // Add CSS to prevent text selection
      document.body.style.userSelect = 'none'
      document.body.style.touchAction = 'none'

      // Add event listeners
      document.addEventListener('selectstart', preventSelection)
      document.addEventListener('touchstart', onTouchStartDoc, {
        passive: true,
      })
      window.addEventListener('mousemove', onMouseMove)
      window.addEventListener('touchmove', onTouchMove, { passive: false })
    } else {
      // Re-enable text selection when not resizing
      document.body.style.userSelect = ''
      document.body.style.touchAction = ''
    }

    return () => {
      // Clean up: re-enable text selection
      document.body.style.userSelect = ''
      document.body.style.touchAction = ''

      // Remove event listeners
      document.removeEventListener('selectstart', preventSelection)
      window.removeEventListener('touchstart', onTouchStartDoc)
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('touchmove', onTouchMove)
    }
  }, [enabled, isResizing, height, touchStart, commitMemberHeight])

  // Return no-op handlers when disabled to avoid unnecessary work in consumers
  if (!enabled) {
    return [0, noop, noop, noop, false] as const
  }

  // `isResizing` is last so the four existing positions keep their meaning for
  // consumers that destructure fewer of them.
  return [
    height,
    onMouseDown,
    onTouchStart,
    onKeyboardResize,
    isResizing,
  ] as const
}
